import { RefObject, useCallback, useEffect, useRef } from "react";
import { useGetTransparency } from "utils/colorHooks";
import useLatestRef from "utils/useLatestRef";

type CompletionFocusPhaseDurationsMs = {
  popLandMs: number;
  popPreLandMs: number;
  popRecoilMs: number;
  popZoomOutMs: number;
  restMs: number;
  splashDecayMs: number;
  splashDelayMs: number;
  splashPeakMs: number;
};

type CompletionFocusScaleConfig = {
  preLand: number;
  recoil: number;
  zoomOut: number;
};

type CompletionFocusShadowOpacityConfig = {
  dropShadow: number;
  innerRing: number;
  outerGlow: number;
};

type CompletionFocusShadowColorConfig = {
  dropShadow: string;
  innerRing: string;
  outerGlow: string;
};

type GetTransparency = (color: string, opacity: number) => string;

type Params = {
  element: HTMLElement;
  getTransparency: GetTransparency;
  isRepeating: boolean;
  phaseDurationsMs?: CompletionFocusPhaseDurationsMs;
  scaleConfig?: CompletionFocusScaleConfig;
  shadowColorConfig?: CompletionFocusShadowColorConfig;
  shadowOpacityConfig?: CompletionFocusShadowOpacityConfig;
};

type UseCompletionFocusFlashParams = {
  phaseDurationsMs?: CompletionFocusPhaseDurationsMs;
  scaleConfig?: CompletionFocusScaleConfig;
  shadowColorConfig?: CompletionFocusShadowColorConfig;
  shadowOpacityConfig?: CompletionFocusShadowOpacityConfig;
  shouldEnablePreview?: boolean;
  targetRef: RefObject<HTMLElement | null>;
};

const DEFAULT_COMPLETION_FOCUS_PHASE_DURATIONS_MS = {
  popLandMs: 50,
  popPreLandMs: 65,
  popRecoilMs: 100,
  popZoomOutMs: 400,
  restMs: 890,
  splashDecayMs: 300,
  splashDelayMs: 2,
  splashPeakMs: 15,
} as const satisfies CompletionFocusPhaseDurationsMs;

const DEFAULT_COMPLETION_FOCUS_SCALE_CONFIG = {
  preLand: 1.04,
  recoil: 0.98,
  zoomOut: 1.07,
} as const satisfies CompletionFocusScaleConfig;

const DEFAULT_COMPLETION_FOCUS_SHADOW_OPACITY_CONFIG = {
  dropShadow: 0.22,
  innerRing: 0.12,
  outerGlow: 0.32,
} as const satisfies CompletionFocusShadowOpacityConfig;

const DEFAULT_COMPLETION_FOCUS_SHADOW_COLOR_CONFIG = {
  dropShadow: "gray.500",
  innerRing: "lightPink",
  outerGlow: "lightPink",
} as const satisfies CompletionFocusShadowColorConfig;

const shouldEnableCompletionFocusFlashDevPreview = false;

function getCompletionFocusTiming(config: CompletionFocusPhaseDurationsMs) {
  Object.entries(config).forEach(([phaseName, phaseMs]) => {
    if (phaseMs < 0) {
      throw new Error(`Expected non-negative timing for phase: ${phaseName}`);
    }
  });

  const totalDurationMs = Object.values(config).reduce(
    (sum, phaseMs) => sum + phaseMs,
    0,
  );
  if (totalDurationMs <= 0) {
    throw new Error("Expected total completion focus animation duration > 0");
  }

  const zoomOutMs = config.popZoomOutMs;
  const recoilMs = zoomOutMs + config.popRecoilMs;
  const preLandMs = recoilMs + config.popPreLandMs;
  const landMs = preLandMs + config.popLandMs;
  const splashPeakMs = landMs + config.splashDelayMs + config.splashPeakMs;
  const splashDecayMs = splashPeakMs + config.splashDecayMs;

  return {
    durationMs: totalDurationMs,
    offsets: {
      land: landMs / totalDurationMs,
      preLand: preLandMs / totalDurationMs,
      recoil: recoilMs / totalDurationMs,
      splashDecay: splashDecayMs / totalDurationMs,
      splashPeak: splashPeakMs / totalDurationMs,
      zoomOut: zoomOutMs / totalDurationMs,
    },
  };
}

function getScaledTransform(baseTransform: string, scale: number) {
  return baseTransform === "none"
    ? `scale(${scale})`
    : `${baseTransform} scale(${scale})`;
}

export default function startCompletionFocusFlashAnimation({
  element,
  getTransparency,
  isRepeating,
  phaseDurationsMs = DEFAULT_COMPLETION_FOCUS_PHASE_DURATIONS_MS,
  scaleConfig = DEFAULT_COMPLETION_FOCUS_SCALE_CONFIG,
  shadowColorConfig = DEFAULT_COMPLETION_FOCUS_SHADOW_COLOR_CONFIG,
  shadowOpacityConfig = DEFAULT_COMPLETION_FOCUS_SHADOW_OPACITY_CONFIG,
}: Params) {
  if (typeof element.animate !== "function") return null;

  const animationElement = element;
  const flashInnerRingColor = getTransparency(
    shadowColorConfig.innerRing,
    shadowOpacityConfig.innerRing,
  );
  const flashOuterGlowColor = getTransparency(
    shadowColorConfig.outerGlow,
    shadowOpacityConfig.outerGlow,
  );
  const flashDropShadowColor = getTransparency(
    shadowColorConfig.dropShadow,
    shadowOpacityConfig.dropShadow,
  );
  const computedStyles = window.getComputedStyle(animationElement);
  const baseBackgroundColor = computedStyles.backgroundColor;
  const baseBoxShadow = computedStyles.boxShadow;
  const baseTransform = computedStyles.transform;
  const timing = getCompletionFocusTiming(phaseDurationsMs);
  const { offsets } = timing;

  animationElement.style.transformOrigin = "center center";
  const startTransform = baseTransform === "none" ? "scale(1)" : baseTransform;
  const zoomOutTransform = getScaledTransform(
    baseTransform,
    scaleConfig.zoomOut,
  );
  const recoilTransform = getScaledTransform(baseTransform, scaleConfig.recoil);
  const preLandTransform = getScaledTransform(
    baseTransform,
    scaleConfig.preLand,
  );

  return animationElement.animate(
    [
      {
        backgroundColor: baseBackgroundColor,
        boxShadow: baseBoxShadow,
        transform: startTransform,
        offset: 0,
      },
      {
        backgroundColor: baseBackgroundColor,
        boxShadow: baseBoxShadow,
        transform: zoomOutTransform,
        offset: offsets.zoomOut,
      },
      {
        backgroundColor: baseBackgroundColor,
        boxShadow: baseBoxShadow,
        transform: recoilTransform,
        offset: offsets.recoil,
      },
      {
        backgroundColor: baseBackgroundColor,
        boxShadow: baseBoxShadow,
        transform: preLandTransform,
        offset: offsets.preLand,
      },
      {
        backgroundColor: baseBackgroundColor,
        boxShadow: baseBoxShadow,
        transform: startTransform,
        offset: offsets.land,
      },
      {
        backgroundColor: baseBackgroundColor,
        boxShadow:
          `0 0 0 1px ${flashInnerRingColor}, ` +
          `0 0 24px 10px ${flashOuterGlowColor}, ` +
          `0 14px 28px ${flashDropShadowColor}`,
        transform: startTransform,
        offset: offsets.splashPeak,
      },
      {
        backgroundColor: baseBackgroundColor,
        boxShadow:
          `0 0 0 1px ${flashInnerRingColor}, ` +
          `0 0 12px 5px ${flashOuterGlowColor}, ` +
          `0 7px 14px ${flashDropShadowColor}`,
        transform: startTransform,
        offset: offsets.splashDecay,
      },
      {
        backgroundColor: baseBackgroundColor,
        boxShadow: baseBoxShadow,
        transform: startTransform,
        offset: 1,
      },
    ],
    {
      duration: timing.durationMs,
      easing: "linear",
      iterations: isRepeating ? Infinity : 1,
    },
  );
}

export function useCompletionFocusFlash({
  phaseDurationsMs = DEFAULT_COMPLETION_FOCUS_PHASE_DURATIONS_MS,
  scaleConfig = DEFAULT_COMPLETION_FOCUS_SCALE_CONFIG,
  shadowColorConfig = DEFAULT_COMPLETION_FOCUS_SHADOW_COLOR_CONFIG,
  shadowOpacityConfig = DEFAULT_COMPLETION_FOCUS_SHADOW_OPACITY_CONFIG,
  shouldEnablePreview = shouldEnableCompletionFocusFlashDevPreview,
  targetRef,
}: UseCompletionFocusFlashParams) {
  const getTransparency = useGetTransparency();
  const flashAnimationRef = useRef<Animation | null>(null);
  const isPreviewRunningRef = useRef(false);
  const latestPhaseDurationsMsRef = useLatestRef(phaseDurationsMs);
  const latestScaleConfigRef = useLatestRef(scaleConfig);
  const latestShadowColorConfigRef = useLatestRef(shadowColorConfig);
  const latestShadowOpacityConfigRef = useLatestRef(shadowOpacityConfig);

  const startCompletionFocusFlash = useCallback(
    (isRepeating: boolean) => {
      const element = targetRef.current;
      if (!element) return false;

      flashAnimationRef.current?.cancel();
      flashAnimationRef.current = startCompletionFocusFlashAnimation({
        element,
        getTransparency,
        isRepeating,
        phaseDurationsMs: latestPhaseDurationsMsRef.current,
        scaleConfig: latestScaleConfigRef.current,
        shadowColorConfig: latestShadowColorConfigRef.current,
        shadowOpacityConfig: latestShadowOpacityConfigRef.current,
      });
      return flashAnimationRef.current !== null;
    },
    [
      getTransparency,
      latestPhaseDurationsMsRef,
      latestScaleConfigRef,
      latestShadowColorConfigRef,
      latestShadowOpacityConfigRef,
      targetRef,
    ],
  );

  const stopCompletionFocusFlash = useCallback(() => {
    flashAnimationRef.current?.cancel();
    flashAnimationRef.current = null;
  }, []);

  useEffect(
    () => () => {
      stopCompletionFocusFlash();
    },
    [stopCompletionFocusFlash],
  );

  useEffect(() => {
    if (!shouldEnablePreview) {
      if (isPreviewRunningRef.current) {
        stopCompletionFocusFlash();
        isPreviewRunningRef.current = false;
      }
      return;
    }

    if (!targetRef.current) {
      if (isPreviewRunningRef.current) {
        stopCompletionFocusFlash();
        isPreviewRunningRef.current = false;
      }
      return;
    }

    if (isPreviewRunningRef.current) return;
    if (!startCompletionFocusFlash(true)) return;
    isPreviewRunningRef.current = true;

    return () => {
      isPreviewRunningRef.current = false;
      stopCompletionFocusFlash();
    };
  });

  return { startCompletionFocusFlash, stopCompletionFocusFlash };
}
